/**
 * Zero-Knowledge Compliance Proofs demo navigation registry (RD-25).
 *
 * Five panels: the security thesis, the witness→ZK-predicate map, the
 * commit–challenge–response protocol, the "Prove Without Reveal" prover game,
 * and the honest foundation/mechanization status. All panels live locally in
 * `../panels/`.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import { defineMonographNavigation } from "@csk/demo-ui/components/monograph-demo-shell";
import { DEMO_COLOR } from "@csk/demo-ui/design-system";
import { OverviewPanel } from "../panels/OverviewPanel.js";
import { WitnessPredicateMapPanel } from "../panels/WitnessPredicateMapPanel.js";
import { ProtocolPanel } from "../panels/ProtocolPanel.js";
import { ProveWithoutRevealPanel } from "../panels/ProveWithoutRevealPanel.js";
import { FoundationPanel } from "../panels/FoundationPanel.js";

export const NAV = [
  {
    id: "overview",
    label: "The Thesis",
    subtitle: "Prove CSK compliance without revealing the witness",
    icon: "◇",
    accent: DEMO_COLOR.violet,
    component: OverviewPanel,
  },
  {
    id: "witness-map",
    label: "Witness → ZK Predicate",
    subtitle: "Each categorical witness, the predicate a ZKP proves of it",
    icon: "⊞",
    accent: DEMO_COLOR.cyan,
    component: WitnessPredicateMapPanel,
  },
  {
    id: "protocol",
    label: "Commit–Challenge–Response",
    subtitle: "The protocol · soundness and zero-knowledge in plain English",
    icon: "⇄",
    accent: DEMO_COLOR.sky,
    component: ProtocolPanel,
  },
  {
    id: "prover",
    label: "Prove Without Reveal",
    subtitle: "Build accepting transcripts · no reveal, no forge",
    icon: "λ",
    accent: DEMO_COLOR.emerald,
    component: ProveWithoutRevealPanel,
  },
  {
    id: "foundation",
    label: "Foundation & Mechanization",
    subtitle: "RD-25 SETTLED · ZKP-1..6 proved · zero postulates",
    icon: "Π",
    accent: DEMO_COLOR.teal,
    component: FoundationPanel,
  },
] as const;

export const NAVIGATION = defineMonographNavigation(NAV, {
  defaultPanelId: "overview",
});
